// src/pages/MarketTable.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  LinearProgress,
  Tooltip,
  Stack,
  Button,
  Typography,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  Skeleton,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthEastIcon from "@mui/icons-material/SouthEast";
import { pctColor, formatCurrency } from "./formatters";
import { getMarketsPage } from "../coingecko";

const ACCENT = "#F0B90B";
const BG = "#263238";
const SURFACE = "rgba(38,50,56,0.85)";
const BORDER = "rgba(255,255,255,0.08)";
const TEXT = "#EAEFF2";
const MUTED = "#B0BEC5";

export default function MarketTable({
  filterMode = "all",
  onSelect,
  vs = "USD",
  category = "",
  pageable = false,
  dense = false,
  limit, // only for dense cards (e.g., Top Gainers)
}) {
  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const isMobile = useMediaQuery("(max-width:900px)");

  // debounce search input for smoother UX
  const qState = useDebouncedState(q, 200);

  // Reset when currency/category changes
  useEffect(() => {
    setRows([]);
    setFiltered([]);
    setPage(1);
    setHasMore(true);
  }, [vs, category]);

  // Load a page (250 per page)
  const loadPage = async (pageNum, append = false, controller) => {
    const data = await getMarketsPage(
      {
        vs: vs.toLowerCase(),
        order: "market_cap_desc",
        perPage: 250,
        page: pageNum,
        changes: "1h,24h,7d",
        category: category || undefined,
        signal: controller?.signal,
      },
      {
        onRefresh: (fresh) => {
          setLastUpdated(new Date());
          if (!append) {
            setRows(Array.isArray(fresh) ? fresh : []);
          } else {
            setRows((prev) => [
              ...prev,
              ...(Array.isArray(fresh) ? fresh : []),
            ]);
          }
        },
      }
    );
    setLastUpdated(new Date());
    if (Array.isArray(data) && data.length > 0) {
      if (!append) setRows(data);
      else setRows((prev) => [...prev, ...data]);
      // If returned less than perPage, likely no more pages
      setHasMore(data.length === 250);
    } else {
      setHasMore(false);
    }
  };

  // Initial + reloads
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    (async () => {
      try {
        setLoading(true);
        await loadPage(1, false, controller);
      } catch {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vs, category]);

  const handleLoadMore = async () => {
    const next = page + 1;
    setPage(next);
    await loadPage(next, true);
  };

  // Search + Filters
  useEffect(() => {
    const base = [...rows];
    const query = qState.trim().toLowerCase();
    const searched = !query
      ? base
      : base.filter(
          (r) =>
            r.name?.toLowerCase().includes(query) ||
            r.symbol?.toLowerCase().includes(query)
        );

    let f = searched;
    if (filterMode === "gainers") {
      f = [...searched].sort(
        (a, b) =>
          (b.price_change_percentage_24h ?? 0) -
          (a.price_change_percentage_24h ?? 0)
      );
    } else if (filterMode === "losers") {
      f = [...searched].sort(
        (a, b) =>
          (a.price_change_percentage_24h ?? 0) -
          (b.price_change_percentage_24h ?? 0)
      );
    } else if (filterMode === "volume") {
      f = [...searched].sort(
        (a, b) => (b.total_volume ?? 0) - (a.total_volume ?? 0)
      );
    }
    setFiltered(limit ? f.slice(0, limit) : f);
  }, [qState, rows, filterMode, limit]);

  // Row component for mobile cards
  const MobileRow = ({ r }) => {
    const p1h = r.price_change_percentage_1h_in_currency ?? 0;
    const p24h = r.price_change_percentage_24h_in_currency ?? 0;
    const p7d = r.price_change_percentage_7d_in_currency ?? 0;

    return (
      <Card
        onClick={() => onSelect?.({ id: r.id, name: r.name })}
        sx={{
          bgcolor: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: 2,
          cursor: "pointer",
          "&:hover": { boxShadow: "0 0 0 1px rgba(240,185,11,0.25)" },
        }}
      >
        <CardContent sx={{ p: dense ? 1 : 1.5 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Avatar
                src={r.image}
                alt={r.symbol}
                sx={{ width: 26, height: 26 }}
              />
              <Typography sx={{ color: TEXT, fontWeight: 800 }}>
                {r.name}
              </Typography>
              <Typography
                sx={{ color: MUTED, textTransform: "uppercase", fontSize: 12 }}
              >
                {r.symbol}
              </Typography>
            </Stack>
            <Tooltip title="Open on CoinGecko">
              <IconButton
                size="small"
                href={`https://www.coingecko.com/en/coins/${r.id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{ color: ACCENT }}
              >
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Divider sx={{ my: 1, borderColor: BORDER }} />

          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            <Stat
              label={`Price (${vs})`}
              value={formatCurrency(r.current_price, vs)}
            />
            <Stat
              label="1h"
              value={`${Math.abs(p1h).toFixed(2)}%`}
              color={pctColor(p1h)}
              arrow={p1h}
            />
            <Stat
              label="24h"
              value={`${Math.abs(p24h).toFixed(2)}%`}
              color={pctColor(p24h)}
              arrow={p24h}
            />
            <Stat
              label="7d"
              value={`${Math.abs(p7d).toFixed(2)}%`}
              color={pctColor(p7d)}
              arrow={p7d}
            />
            <Stat label="Mkt Cap" value={formatCurrency(r.market_cap, vs)} />
            <Stat label="24h Vol" value={formatCurrency(r.total_volume, vs)} />
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const Stat = ({ label, value, color, arrow }) => (
    <Stack spacing={0.25} sx={{ minWidth: 110 }}>
      <Typography sx={{ color: MUTED, fontSize: 12 }}>{label}</Typography>
      <Typography sx={{ color: color || TEXT, fontWeight: 800 }}>
        {typeof arrow === "number" ? (
          <>
            {arrow >= 0 ? (
              <NorthEastIcon fontSize="inherit" />
            ) : (
              <SouthEastIcon fontSize="inherit" />
            )}{" "}
            {value}
          </>
        ) : (
          value
        )}
      </Typography>
    </Stack>
  );

  return (
    <Box>
      {!dense && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <TextField
            placeholder="Search by name or symbol (e.g., BTC, ETH, SOL)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            size="small"
            sx={{
              width: { xs: "100%", sm: 360 },
              "& .MuiOutlinedInput-root": { color: TEXT },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            {lastUpdated && (
              <Typography variant="caption" sx={{ color: MUTED }}>
                Updated: {lastUpdated.toLocaleTimeString()}
              </Typography>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={() => setQ("")}
              sx={{ borderColor: BORDER, color: TEXT }}
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      )}

      {loading && rows.length === 0 ? (
        <Stack spacing={1}>
          <LinearProgress />
          {!dense && (
            <>
              <Skeleton
                variant="rounded"
                height={48}
                sx={{ bgcolor: "rgba(255,255,255,0.06)" }}
              />
              <Skeleton
                variant="rounded"
                height={48}
                sx={{ bgcolor: "rgba(255,255,255,0.06)" }}
              />
            </>
          )}
        </Stack>
      ) : !isMobile && !dense ? (
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            maxHeight: 520,
            overflowY: "auto",
            overflowX: "hidden",
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    bgcolor: SURFACE,
                    borderBottom: `1px solid ${BORDER}`,
                    color: TEXT,
                    fontWeight: 800,
                  },
                }}
              >
                <TableCell sx={{ minWidth: 200 }}>Asset</TableCell>
                <TableCell align="right" sx={{ minWidth: 120 }}>
                  Price ({vs})
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>
                  1h
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>
                  24h
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>
                  7d
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 160 }}>
                  Market Cap
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 160 }}>
                  24h Volume
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 80 }}>
                  Links
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& td": { borderBottom: `1px solid ${BORDER}`, color: TEXT },
                "& tr:hover": { backgroundColor: "rgba(255,255,255,0.02)" },
              }}
            >
              {filtered.map((r) => {
                const p1h = r.price_change_percentage_1h_in_currency ?? 0;
                const p24h = r.price_change_percentage_24h_in_currency ?? 0;
                const p7d = r.price_change_percentage_7d_in_currency ?? 0;
                return (
                  <TableRow
                    hover
                    key={r.id}
                    sx={{ cursor: "pointer" }}
                    onClick={() => onSelect?.({ id: r.id, name: r.name })}
                  >
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          src={r.image}
                          alt={r.symbol}
                          sx={{ width: 24, height: 24 }}
                        />
                        <strong>{r.name}</strong>
                        <Box
                          sx={{
                            opacity: 0.6,
                            ml: 0.5,
                            textTransform: "uppercase",
                            fontSize: 12,
                          }}
                        >
                          {r.symbol}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(r.current_price, vs)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: pctColor(p1h) }}>
                      {p1h >= 0 ? (
                        <NorthEastIcon fontSize="inherit" />
                      ) : (
                        <SouthEastIcon fontSize="inherit" />
                      )}{" "}
                      {Math.abs(p1h).toFixed(2)}%
                    </TableCell>
                    <TableCell align="right" sx={{ color: pctColor(p24h) }}>
                      {p24h >= 0 ? (
                        <NorthEastIcon fontSize="inherit" />
                      ) : (
                        <SouthEastIcon fontSize="inherit" />
                      )}{" "}
                      {Math.abs(p24h).toFixed(2)}%
                    </TableCell>
                    <TableCell align="right" sx={{ color: pctColor(p7d) }}>
                      {p7d >= 0 ? (
                        <NorthEastIcon fontSize="inherit" />
                      ) : (
                        <SouthEastIcon fontSize="inherit" />
                      )}{" "}
                      {Math.abs(p7d).toFixed(2)}%
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(r.market_cap, vs)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(r.total_volume, vs)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Open on CoinGecko">
                        <IconButton
                          size="small"
                          href={`https://www.coingecko.com/en/coins/${r.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: ACCENT }}
                        >
                          <OpenInNewIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Pagination: Load more */}
          {pageable && hasMore && (
            <Box sx={{ p: 1.5, display: "flex", justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                sx={{ borderColor: BORDER, color: TEXT }}
              >
                Load more (page {page + 1})
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        // Mobile / Dense cards
        <Stack spacing={1.25}>
          {filtered.map((r) => (
            <MobileRow key={r.id} r={r} />
          ))}
          {pageable && hasMore && (
            <Box sx={{ pt: 0.5, display: "flex", justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                sx={{ borderColor: BORDER, color: TEXT }}
              >
                Load more (page {page + 1})
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}

// Small debounce hook
function useDebouncedState(value, delay = 200) {
  const [v, setV] = useState(value);
  const ref = useRef();
  useEffect(() => {
    clearTimeout(ref.current);
    ref.current = setTimeout(() => setV(value), delay);
    return () => clearTimeout(ref.current);
  }, [value, delay]);
  return v;
}
